/**
 * BodyCompositionTimeline — Phase 2.2B Session 2
 *
 * Three line charts (BF%, Fat Mass, Lean Mass) plotted across time.
 * Points are colored per measurement method (legend).
 */
import React, { useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend, Scatter, ComposedChart } from 'recharts';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';

const METHOD_COLOR = {
  SKINFOLDS: '#06b6d4',
  BIA: '#a78bfa',
  DEXA: '#10b981',
  MANUAL: '#a1a1aa',
};

const MetricChart = ({ title, dataKey, suffix, rows }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">{title}</div>
    <div className="h-52 min-h-[13rem]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
          <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 11 }} />
          <YAxis stroke="#71717a" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}${suffix}`} />
          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a' }} formatter={(v) => `${Number(v).toFixed(1)}${suffix}`} />
          <Line type="monotone" dataKey={dataKey} stroke="#52525b" strokeWidth={1.5} dot={false} isAnimationActive={false} />
          {['SKINFOLDS', 'BIA', 'DEXA', 'MANUAL'].map((m) => (
            <Scatter
              key={m} name={m}
              data={rows.filter((r) => r.method === m && r[dataKey] !== null && r[dataKey] !== undefined)}
              dataKey={dataKey}
              fill={METHOD_COLOR[m]}
              shape="circle"
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const BodyCompositionTimeline = ({ rows }) => {
  const { language } = useLanguage();

  // Normalize: backend stores raw rows with body_fat_pct_final, fat_mass_kg_final, lean_mass_kg_final
  // (full rows from GET /nutrition/body-composition), but the analytics dashboard timeline already
  // exposes body_fat_pct, fat_mass_kg, lean_mass_kg with the *_final values mapped. Support both.
  const series = useMemo(() => {
    const arr = (rows || []).map((r) => ({
      date: r.date,
      method: r.method,
      body_fat_pct: r.body_fat_pct ?? r.body_fat_pct_final ?? null,
      fat_mass_kg: r.fat_mass_kg ?? r.fat_mass_kg_final ?? null,
      lean_mass_kg: r.lean_mass_kg ?? r.lean_mass_kg_final ?? null,
    }));
    return arr.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [rows]);

  if (series.length === 0) {
    return null;
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="bodycomp-timeline">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-zinc-100">
          {language === 'IT' ? 'Andamento composizione corporea' : 'Body composition trend'}
        </h3>
        <div className="flex flex-wrap gap-2">
          {['SKINFOLDS', 'BIA', 'DEXA', 'MANUAL'].map((m) => (
            <Badge
              key={m}
              className="border bg-transparent text-xs"
              style={{ borderColor: METHOD_COLOR[m], color: METHOD_COLOR[m] }}
              data-testid={`legend-${m}`}
            >
              ● {m}
            </Badge>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricChart title="BF %" dataKey="body_fat_pct" suffix=" %" rows={series} />
        <MetricChart title={language === 'IT' ? 'Massa grassa' : 'Fat mass'} dataKey="fat_mass_kg" suffix=" kg" rows={series} />
        <MetricChart title={language === 'IT' ? 'Massa magra' : 'Lean mass'} dataKey="lean_mass_kg" suffix=" kg" rows={series} />
      </div>
    </Card>
  );
};

export default BodyCompositionTimeline;
